import HealthScoreQuestionnaire from "@/features/HealthScore/components/HealthScoreQuestionnaire";
import {
  Question,
  QuestionnaireAnswer,
  QuestionType,
  QuestionnaireType,
  IntenseExerciseMinutes,
  SleepHours,
  FruitVegServings,
  AlcoholConsumption,
  SmokerStatus,
} from "@/features/HealthScore/components/HealthScoreQuestionnaire/types";
import useGetHealthScoreQuestionnaire, {
  healthScoreQuestionnaireQueryKey,
} from "@/features/HealthScore/components/HealthScoreQuestionnaire/hooks/useGetHealthScoreQuestionnaire";
import {
  submitHealthScoreQuestionnaire,
  SubmitHealthScoreQuestionnairePayload,
} from "@/services/core-api-adapter";
import DefaultError from "@/features/Common/components/DefaultError";
import { useQueryClient } from "@tanstack/react-query";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import useGetHealthProfile from "../../hooks/useGetHealthProfile";
import { healthScoreSummaryQueryKey } from "../../hooks/useGetHealthScoreSummary";

export default function LifeStyleQuestionnaire() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { refetchHealthProfile: refetch } = useGetHealthProfile();
  const fromExercise = location.state?.from === "exercise";
  const fromSleep = location.state?.from === "sleep";
  const [submitError, setSubmitError] = useState(false);

  const {
    questionnaireData: initialAnswers,
    isQuestionnaireLoading,
    questionnaireFetchError,
    refetchQuestionnaire,
  } = useGetHealthScoreQuestionnaire(QuestionnaireType.LIFESTYLE);

  const LIFESTYLE_QUESTIONS: Question[] = [
    {
      type: QuestionType.INTENSE_EXERCISE,
      meta: {
        values: [
          IntenseExerciseMinutes.ZERO_TO_150,
          IntenseExerciseMinutes.ONE_FIFTY_TO_300,
          IntenseExerciseMinutes.THREE_HUNDRED_PLUS,
        ],
      },
    },
    {
      type: QuestionType.SLEEP,
      meta: {
        values: [
          SleepHours.LESS_THAN_7,
          SleepHours.SEVEN_TO_9,
          SleepHours.MORE_THAN_9,
        ],
      },
    },
    {
      type: QuestionType.FRUIT_VEG_CONSUMPTION,
      meta: {
        values: [
          FruitVegServings.ONE,
          FruitVegServings.TWO,
          FruitVegServings.THREE,
        ],
      },
    },
    {
      type: QuestionType.ALCOHOL_CONSUMPTION,
      meta: {
        values: [
          AlcoholConsumption.NONE,
          AlcoholConsumption.ONE,
          AlcoholConsumption.TWO,
          AlcoholConsumption.TWO_PLUS,
        ],
      },
    },
    {
      type: QuestionType.SMOKER_STATUS,
      meta: {
        values: [
          SmokerStatus.NEVER,
          SmokerStatus.STOPPED,
          SmokerStatus.REGULAR,
        ],
      },
    },
  ];

  const navigateToHealthScore = () => {
    if (fromExercise) {
      navigate("/home/health-score", { state: { openExercise: true } });
    } else if (fromSleep) {
      navigate("/home/health-score", { state: { openSleep: true } });
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async (answers: QuestionnaireAnswer[]) => {
    const payload: SubmitHealthScoreQuestionnairePayload = {
      questionnaireType: QuestionnaireType.LIFESTYLE,
      value: answers,
    };
    try {
      await submitHealthScoreQuestionnaire(payload);
      await queryClient.invalidateQueries({
        queryKey: healthScoreQuestionnaireQueryKey(QuestionnaireType.LIFESTYLE),
      });
      await refetch();
      await queryClient.invalidateQueries({
        queryKey: healthScoreSummaryQueryKey,
      });
      navigateToHealthScore();
    } catch {
      setSubmitError(true);
    }
  };

  const handleClose = () => {
    navigateToHealthScore();
  };

  const renderQuestionnaireError = (onTryAgain: () => void) => (
    <DefaultError onComeBackLater={handleClose} onTryAgain={onTryAgain}>
      <Typography variant="h2" pb={2}>
        {t("common.genericErrorMessage")}
      </Typography>
    </DefaultError>
  );

  if (isQuestionnaireLoading) {
    return <FullscreenLoadingIndicator mainIconType="face" />;
  }

  if (questionnaireFetchError) {
    return renderQuestionnaireError(() => refetchQuestionnaire());
  }

  if (submitError) {
    return renderQuestionnaireError(() => setSubmitError(false));
  }

  return (
    <HealthScoreQuestionnaire
      questions={LIFESTYLE_QUESTIONS}
      translationPrefix="Lifestyle.questionnaire"
      onSubmit={handleSubmit}
      onClose={handleClose}
      title={t("Lifestyle.questionnaire.title")}
      initialAnswers={initialAnswers ?? {}}
    />
  );
}
